import { uniq } from 'lodash-es';
import { FieldMetadata } from 'models/forms/fields';
import type { ProductLayoutBlockNode, ProductLayoutNode, ProductLayoutTree } from 'models/forms/FormResult';
import { LayoutCustomizationBehavior } from 'models/metadata/shared.definition';
import { createSelector } from 'reselect';
import { Width } from 'state/product-description/types';
import { getCurrentTabIdFromRouter } from 'state/selectors/common';
import { AppState } from 'state/state.model';
import {
  emptyProductLayoutOverride,
  type FieldId,
  type ProductLayoutOverride,
} from 'state/userPreferences/userPreferences.model';
import { getUserPreferenceProductLayoutOverride } from 'state/userPreferences/userPreferences.selectors';
import {
  selectEssentialWorkspaceTabProductVariation,
  selectWorkspaceEssentialTabProductLayout,
} from 'state/workspaces/essential/selectors';

import { LayoutField, LayoutSection } from './model';
import { isHiddenNode, isProductLayoutBlockNode } from './selectors.shared';

export const selectCustomProductLayoutSections = createSelector(
  [getCurrentTabIdFromRouter, (state: AppState) => state],
  (tabId, state): LayoutSection[] => {
    if (!tabId) {
      return [];
    }

    const productLayout = selectWorkspaceEssentialTabProductLayout(state, tabId);
    if (!productLayout?.nodes?.length) {
      return [];
    }

    const productVariation = selectEssentialWorkspaceTabProductVariation(state, tabId);
    const productLayoutPreferences =
      getUserPreferenceProductLayoutOverride(state, productVariation) ?? emptyProductLayoutOverride;

    return buildCustomProductLayoutSections(productLayout, productLayoutPreferences);
  },
);

export const selectDefaultCustomProductLayoutSections = createSelector(
  [getCurrentTabIdFromRouter, (state: AppState) => state],
  (tabId, state): LayoutSection[] => {
    if (!tabId) {
      return [];
    }

    const productLayout = selectWorkspaceEssentialTabProductLayout(state, tabId);
    if (!productLayout?.nodes?.length) {
      return [];
    }

    return buildCustomProductLayoutSections(productLayout, emptyProductLayoutOverride);
  },
);

const buildCustomProductLayoutSections = (
  productLayout: ProductLayoutTree,
  productLayoutPreferences: ProductLayoutOverride,
): LayoutSection[] => {
  const fieldOverrides = productLayoutPreferences.fieldOverrides ?? {};

  return productLayout.nodes
    .filter(n => !isHiddenNode(n))
    .filter(isProductLayoutBlockNode)
    .map(sectionNode => {
      const fields = getLeafFieldsFromBlock(sectionNode);
      const availableFieldIds = fields.map(field => field.id);
      const orderedFieldIds = uniq(getOrderedFieldIds(productLayoutPreferences, availableFieldIds));
      const fieldsById = new Map(fields.map(field => [field.id, field]));

      const blockCustomizationBehavior = sectionNode.definition.layoutCustomizationBehavior;

      const getLayoutCustomizationBehavior = (field: FieldMetadata | undefined): LayoutCustomizationBehavior =>
        field?.layoutCustomizationBehavior ?? blockCustomizationBehavior ?? 'Customizable';

      const layoutFields = orderedFieldIds
        .map((fieldId, order): LayoutField | undefined => {
          const field = fieldsById.get(fieldId);
          if (!field) {
            return undefined;
          }

          const behavior = getLayoutCustomizationBehavior(field);

          const hasVisibilityPreference =
            productLayoutPreferences.visibleFields.includes(fieldId) ||
            productLayoutPreferences.additionalFields.includes(fieldId);

          const initialWidth: Width = field.width ?? 'full';
          const width: Width = fieldOverrides[fieldId] ?? initialWidth;

          return {
            fieldId: field.id,
            isOrderable: behavior === 'Customizable' || behavior === 'Locked',
            isPinned: productLayoutPreferences.pinnedFields.includes(fieldId),
            isVisible: hasVisibilityPreference
              ? productLayoutPreferences.visibleFields.includes(fieldId)
              : !field.isForAdvancedMode,
            isAlwaysVisible: behavior === 'Locked',
            label: field.translationKey ?? field.id,
            order,
            width,
            initialWidth,
          };
        })
        .filter((field): field is LayoutField => field !== undefined);

      return {
        sectionId: sectionNode.id,
        label: sectionNode.id,
        fields: layoutFields,
      };
    });
};

function getLeafFieldsFromBlock(sectionNode: ProductLayoutBlockNode): FieldMetadata[] {
  const fields: FieldMetadata[] = [];

  const populate = (node: ProductLayoutNode) => {
    if (isProductLayoutBlockNode(node)) {
      for (const child of node.childrens) {
        populate(child);
      }
    } else {
      fields.push(node.definition);
    }
  };

  for (const child of sectionNode.childrens) {
    populate(child);
  }

  return fields;
}

const getOrderedFieldIds = (
  productLayoutPreferences: ProductLayoutOverride,
  availableFieldIds: FieldId[],
): FieldId[] => {
  const orderedPreferenceFieldIds = getOrderedUniqueFieldIds(productLayoutPreferences).filter(fieldId =>
    availableFieldIds.includes(fieldId),
  );

  if (!orderedPreferenceFieldIds.length) {
    return availableFieldIds;
  }

  const remainingFieldIds = availableFieldIds.filter(fieldId => !orderedPreferenceFieldIds.includes(fieldId));
  return [...orderedPreferenceFieldIds, ...remainingFieldIds];
};

const getOrderedUniqueFieldIds = ({
  visibleFields,
  additionalFields,
  pinnedFields,
}: ProductLayoutOverride): FieldId[] => {
  const orderedFieldIds = [...visibleFields, ...additionalFields, ...pinnedFields];
  return Array.from(new Set(orderedFieldIds));
};
